import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const allotmentKeys = {
  all: ['allotments'],
  lists: () => [...allotmentKeys.all, 'list'],
  details: () => [...allotmentKeys.all, 'detail'],
  detail: (id) => [...allotmentKeys.details(), id],
};

export const useAllotments = ({ includeInactive = false } = {}) => {
  return useQuery({
    queryKey: [...allotmentKeys.lists(), includeInactive],
    queryFn: async () => {
      const response = await apiClient.get('allotments', {
        params: { include_inactive: includeInactive },
      });
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

export const useAllotment = (id, options = {}) => {
  return useQuery({
    queryKey: allotmentKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`allotments/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

export const useCreateAllotment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (allotmentData) => {
      const isFormData = allotmentData instanceof FormData;
      const response = await apiClient.post('allotments', allotmentData, {
        headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : undefined,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: allotmentKeys.lists() });
    },
    onError: (error) => {
      console.error('Error creating allotment:', error);
    },
  });
};

export const useUpdateAllotment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, allotmentData }) => {
      const isFormData = allotmentData instanceof FormData;
      const response = await apiClient.put(`allotments/${id}`, allotmentData, {
        headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : undefined,
      });
      return response.data;
    },
    onSuccess: (data, { id }) => {
      queryClient.invalidateQueries({ queryKey: allotmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: allotmentKeys.detail(id) });
    },
    onError: (error) => {
      console.error('Error updating allotment:', error);
    },
  });
};

export const useDeleteAllotment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`allotments/${id}`);
      return response.data;
    },
    onSuccess: (data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: allotmentKeys.lists() });
      queryClient.removeQueries({ queryKey: allotmentKeys.detail(deletedId) });
    },
    onError: (error) => {
      console.error('Error deleting allotment:', error);
    },
  });
};
